/**
 * Grant-aware console access (W2-CONSOLES, D364) — which Admin screens an operator can actually work, where it
 * lands after signing in, and what its menu lists.
 *
 * The wave-1 X-ray (`docs/archive/programs/06_UX_XRAY_WAVE1_PROGRAM.md` §8.1) found that login never looks at grants: every
 * persona signed in, was sent to Overview, and Overview calls `GET /v1/ops/dashboard` under `ops:view`. A
 * SERVICE_AGENT (atendente) and a FINANCE operator do not hold that word, so the first screen after login was a
 * 403 — and the menu offered every other screen they could not open either.
 *
 * This module is the client-side **filter**, never the authority: the server re-authorizes every call, and a
 * screen the filter allows can still be refused (then the screen shows the "no permission" state instead of a raw
 * code). It reads the operator's own grants from `GET /v1/operator/grants`, which carry the permission words each
 * grant holds, and mirrors the two target kinds of the server's route table
 * (`services/vello-api/src/operator-route-permissions.ts`):
 *
 * - `PLATFORM` — only a GLOBAL grant covers it (a country/city admin keeps today's deny);
 * - `GEO` — a GLOBAL, COUNTRY or CITY grant covers it, so a market-scoped atendente works inside its geography.
 *
 * A MERCHANT or FLEET grant covers neither, so a persona tenant grant never opens a platform screen. Words are
 * typed as `DomainPermission`, so a screen can never declare a word no route knows; the pairing itself is
 * reviewed against the server table.
 *
 * **What a drift in `SCREEN_POLICIES` costs** (review of D364). It can never grant access — the server
 * authorizes every call. But `ScreenRouter` also renders `AccessDeniedScreen` in place of a screen this table
 * refuses, so a row that is STRICTER than its route blocks an operator the server would have served: the drift
 * is not only a menu nuisance in that direction. Two rows are knowingly stricter or looser than their route and
 * are listed on the table itself; anything else that drifts must be corrected here, not worked around in a
 * screen. A cross-check test against the server table (it needs a shared export) is the durable guard.
 */
import { type OperatorGrant } from '@vello/api-client';
import { type DomainPermission, permissionGranted, permissionsForRole, roleAllowsScopeType } from '@vello/rbac';

/**
 * The target kind a screen's permission is checked at (mirrors the server's route table). `COUNTRY` (D384) is a GEO
 * route the server authorizes at a COUNTRY (accounts are country-level data): a GLOBAL or COUNTRY grant covers it, a
 * CITY grant never does — so a city-only atendente's menu no longer offers a directory that answers 403.
 */
export type ScreenTarget = 'PLATFORM' | 'GEO' | 'COUNTRY';

export interface ScreenPolicy {
  /** The word the screen's main read asks the server for. */
  readonly permission: DomainPermission;
  readonly target: ScreenTarget;
  /**
   * Review of D401 (W6N-01): words a screen ALSO needs at the same target before it can render anything — a screen
   * whose first read (e.g. the merchant list it discovers its subject from) is refused is unusable, so offering it on
   * the main word alone opened a screen that answered 403. Every word listed here must be granted.
   */
  readonly alsoRequires?: readonly DomainPermission[];
}

/**
 * Admin route → the word its main read needs, taken from the server's route table. Routes with no policy are
 * always reachable (`about`, and the unauthenticated routes).
 *
 * Where a screen reads several surfaces, the policy names the one that renders the page: the screen still degrades
 * per-section when a secondary read is refused.
 */
export const SCREEN_POLICIES: Readonly<Record<string, ScreenPolicy>> = {
  dashboard: { permission: 'ops:view', target: 'GEO' }, // GET /v1/ops/dashboard
  live_map: { permission: 'ops:view', target: 'GEO' }, // GET /v1/ops/map/fleet?market= (D471); the dispatch candidates are shown only with the dispatch words
  rides: { permission: 'ride:view', target: 'GEO' }, // POST /v1/ops/ride-support/rides/search (D413)
  rides_grid: { permission: 'ride:view', target: 'GEO' }, // GET /v1/ops/rides?market= (D472, A-11)
  report_catalog: { permission: 'report:view', target: 'GEO' }, // GET /v1/ops/reports (D472, P-07)
  audit_trail: { permission: 'audit:inspect', target: 'GEO' }, // GET /v1/ops/audit (D472, A-14)
  ride_detail: { permission: 'ride:view', target: 'GEO' },
  dispatch: { permission: 'ops:view', target: 'GEO' }, // the dispatch queue is the ops dashboard feed
  courier_jobs: { permission: 'courier_job:view', target: 'GEO' }, // GET /v1/courier-dispatch/jobs?marketId= (D378)
  cases: { permission: 'support:view', target: 'GEO' }, // GET /v1/support/tickets
  case_detail: { permission: 'support_ticket:view', target: 'GEO' }, // GET /v1/support/tickets/:id (D384)
  disputes: { permission: 'dispute:view', target: 'PLATFORM' },
  safety: { permission: 'safety:view', target: 'PLATFORM' },
  // D384: the directory reads accounts at a COUNTRY (D355) — a CITY grant is refused, so it is not offered.
  driver_directory: { permission: 'account:view', target: 'COUNTRY' }, // POST /v1/ops/users/search (D413; GET browses)
  driver_detail: { permission: 'account:view', target: 'COUNTRY' },
  // KNOWINGLY LOOSER than its route: `GET /v1/kyc/review-queue` is `KYC_REVIEWER`, and under D364 a CITY grant
  // reviews NOTHING. A city-scoped reviewer is therefore still offered this entry and then refused by the server
  // (and the refusal reads as "wrong selected scope"). Left looser rather than stricter on purpose: a stricter row
  // would hide the queue from an authority the server does grant.
  driver_approvals: { permission: 'document:review', target: 'GEO' }, // the KYC reviewer queue (country-scoped)
  driver_documents: { permission: 'document:view', target: 'GEO' }, // GET /v1/admin/driver-eligibility?marketId= (D437)
  // W9-DRIVER-REGISTRY (D481): the registration grid and form (`driver:view` / `driver:register`, GEO) and the central document query.
  driver_registry: { permission: 'driver:view', target: 'GEO' }, // GET /v1/admin/driver-registry?marketId=
  driver_register: { permission: 'driver:register', target: 'GEO' }, // POST /v1/admin/driver-registry
  driver_documents_all: { permission: 'document:view', target: 'GEO' }, // GET /v1/admin/driver-documents?marketId=
  provider_services: { permission: 'driver:view', target: 'GEO' }, // D883: GET /v1/admin/provider-services/queue?marketId= (GEO)
  // W9-REPUTATION (D478): the suspensions list reads with the driver's word (the rider tab with `rider:view`, checked by the server); the console reads with `relationship:view`.
  reputation_suspensions: { permission: 'driver:view', target: 'GEO' }, // GET /v1/ops/driver-suspensions?country=
  reputation_relationships: { permission: 'relationship:view', target: 'GEO' }, // GET /v1/ops/relationships/overview
  passengers: { permission: 'account:view', target: 'COUNTRY' },
  passenger_detail: { permission: 'account:view', target: 'COUNTRY' },
  companies: { permission: 'merchant:view', target: 'PLATFORM' },
  finance: { permission: 'ledger:view', target: 'PLATFORM' }, // GET /v1/payments/reconcile
  finance_payouts: { permission: 'payout:view', target: 'PLATFORM' },
  finance_partner_billing: { permission: 'partner_billing:view', target: 'PLATFORM' }, // GET /v1/admin/partner-accounts (D368)
  tariff_categories: { permission: 'pricing:view', target: 'PLATFORM' },
  tariff_dynamic: { permission: 'pricing:view', target: 'PLATFORM' },
  service_categories: { permission: 'service_category:view', target: 'GEO' }, // GET /v1/config/service-categories/markets/:marketId (D462, GEO)
  campaigns: { permission: 'campaign:view', target: 'PLATFORM' },
  registries: { permission: 'config:view', target: 'PLATFORM' },
  // KNOWINGLY STRICTER than its read: `GET /v1/config/markets` needs no word, but this screen exists to
  // register and transition markets, which is platform-wide. A geographic admin therefore gets the "no permission"
  // state here instead of a catalog it can read but not act on.
  markets: { permission: 'market:manage', target: 'PLATFORM' },
  integrations: { permission: 'integration:view', target: 'PLATFORM' },
  integrations_otp_setup: { permission: 'integration:manage', target: 'PLATFORM' },
  // D429: GET /v1/config/widgets (`widget:view`, PLATFORM). Writes need `widget:manage` — the screen hides them otherwise.
  integrations_widgets: { permission: 'widget:view', target: 'PLATFORM' },
  security: { permission: 'security:view', target: 'PLATFORM' },
  // D449 (A-05): GET /v1/admin/operators — `operator:manage` at the operator's country, or platform-wide. `my_access` has no
  // row on purpose: every operator manages its own second factor.
  operators: { permission: 'operator:manage', target: 'COUNTRY' },
  // D475: custom access profiles are platform-level (a role's words are no country's) — `access_profile:manage` at the platform.
  access_profiles: { permission: 'access_profile:manage', target: 'PLATFORM' },
  // D597 (MAN-ACCESS, ADR-0120): list/grant/revoke under the one word `manual_access:manage`, platform target, SUPER_ADMIN only.
  manual_access: { permission: 'manual_access:manage', target: 'PLATFORM' },
  // D475 (E-001/E-005, X-09): `account:manage` registers a rider and proposes a phone change, at the account's country.
  passenger_registry: { permission: 'account:manage', target: 'COUNTRY' },
  // D513 (C-031): the counts come from `POST /v1/ops/users/search` (`limit=1`, the `total`) — the directory's own word and scope.
  providers_overview: { permission: 'account:view', target: 'COUNTRY' },
  customers_overview: { permission: 'account:view', target: 'COUNTRY' },
  // D400: the governed-settings console reads the whole registry, so the READ is platform-wide (the server's rows
  // are `config:view` PLATFORM); a write authorizes at the bucket, which the server checks per call.
  governed_settings: { permission: 'config:view', target: 'PLATFORM' },
  // D631: the same registry read as the governed rules (the area filters its six keys); a write authorizes at the bucket, per call.
  central_ai_settings: { permission: 'config:view', target: 'PLATFORM' },
  // D639: GET /v1/ai/spend authorizes `ai_spend:view` at the platform (the recorded exchange rate needs `ai_spend:manage`; the screen hides it otherwise).
  ai_spend: { permission: 'ai_spend:view', target: 'PLATFORM' },
  // D874: GET /v1/config/integrations/geo/spend authorizes `integration:view` at the platform (the same word as the maps and routes integration).
  maps_spend: { permission: 'integration:view', target: 'PLATFORM' },
  // D440 (ADR-0072): GET /v1/admin/legal-documents authorizes `legal_document:view` at the chosen COUNTRY (the server
  // re-checks the country on every call; drafting and publishing need `legal_document:propose` / `:approve`).
  legal_documents: { permission: 'legal_document:view', target: 'PLATFORM' },
  // GET /v1/admin/career-openings authorizes `career_opening:view` at the chosen COUNTRY (the server re-checks it on every call; writing,
  // opening and closing need `career_opening:manage` - the screen hides those actions otherwise).
  career_openings: { permission: 'career_opening:view', target: 'COUNTRY' },
  notifications: { permission: 'notification:manage', target: 'PLATFORM' },
  analytics: { permission: 'report:view', target: 'PLATFORM' },
  audit: { permission: 'support:view', target: 'GEO' }, // GET /v1/cases/:type/:id/audit
  food_ops: { permission: 'food_ops:view', target: 'PLATFORM' },
  food_review: { permission: 'merchant_review:view', target: 'PLATFORM' },
  food_ads: { permission: 'ads:view', target: 'PLATFORM' },
  food_fulfillment: { permission: 'fulfillment:view', target: 'PLATFORM' },
  // ---- W6-ADMIN-SCREENS (D401) -------------------------------------------------------------------
  // A MERCHANT-targeted route is mirrored here as PLATFORM: a GLOBAL grant covers every tenant
  // (`operator-gate.ts`), and a MERCHANT/FLEET tenant grant opens no Admin screen at all (see the header), so
  // PLATFORM is exactly the authority that can work these consoles. Getting this wrong in the other direction —
  // declaring a GEO target — would offer a city-scoped atendente a screen whose first read answers 403.
  food_orders: { permission: 'order:view', target: 'PLATFORM' }, // GET /v1/food/orders (MERCHANT-targeted route)
  food_postsale: { permission: 'postsale:view', target: 'PLATFORM' }, // GET /v1/food-postsale/cases
  // Review W6N-01: the store console discovers its subject from `GET /v1/food-merchant/merchants`
  // (`merchant:view`) before its own read, so it needs that word too (the statements console no longer does — D405).
  food_stores: { permission: 'store:view', target: 'PLATFORM', alsoRequires: ['merchant:view'] }, // GET /v1/store-profile/merchants/:id/profiles (MERCHANT)
  send_deliveries: { permission: 'delivery:view', target: 'PLATFORM' }, // GET /v1/deliveries?status=&marketId=
  send_claims: { permission: 'claim:view', target: 'PLATFORM' }, // GET /v1/delivery-ops/claims
  // D405 (owner decision on review W6N-01): the merchant picker reads the settlement-scoped list
  // `GET /v1/merchant-finance/merchants` (`settlement:view`), not the directory, so the screen needs no
  // `merchant:view` and FINANCE — which holds settlement:view but not merchant:view — is offered it.
  finance_merchant_statements: { permission: 'settlement:view', target: 'PLATFORM' }, // GET /v1/merchant-finance/... (MERCHANT)
  // ---- W6-REFUND-FOUR-EYES (D398) ----------------------------------------------------------------
  finance_refund_approvals: { permission: 'refund:view', target: 'PLATFORM' }, // GET /v1/refund-approvals
  // ---- W7-CASH-HANDOVER-CONSOLE (D411) ------------------------------------------------------------
  finance_cash_handovers: { permission: 'payout:view', target: 'PLATFORM' }, // GET /v1/cash-handovers (D392 A2)
  // D459 (ADR-0074): the prepaid wallets and the driver groups, GEO (market-bound) reads.
  finance_wallets: { permission: 'wallet:view', target: 'GEO' }, // GET /v1/admin/wallet/wallets?market=
  // D737b (ADR-0150): the rider's balance - the approvals inbox of a market, GEO (market-bound) read.
  finance_rider_wallet: { permission: 'rider_wallet:approve', target: 'GEO' }, // GET /v1/ops/rider-wallet/adjustments?market=
  // D489b (ADR-0102): the Incentive Fund's balances, GEO (market-bound) read.
  finance_incentive_fund: { permission: 'incentive_fund:view', target: 'GEO' }, // GET /v1/admin/incentive-fund/balances?market=
  finance_incentive_offers: { permission: 'incentive_lever:view', target: 'GEO' }, // D490: GET /v1/admin/active-incentives?market=
  finance_incentive_controller: { permission: 'incentive_lever:view', target: 'GEO' }, // D495: GET /v1/admin/incentive-controller?market=
  finance_coupon_campaigns: { permission: 'coupon_campaign:view', target: 'GEO' }, // D493: GET /v1/admin/coupon-campaigns?market=
  referrals: { permission: 'campaign:view', target: 'GEO' }, // D723: GET /v1/driver-referral-campaigns?market= and GET /v1/referral-campaigns?market=
  // D469 (ADR-0087): the driver settlement modes, GEO (market-bound) reads.
  finance_settlement: { permission: 'wallet:view', target: 'GEO' }, // GET /v1/admin/settlement/report?market=
  driver_groups: { permission: 'driver:view', target: 'GEO' }, // GET /v1/admin/driver-groups?market=
  // D536 (AN-RIDER-DEBTS): the market-level indicators are the BI read word; the people on those screens are asked for separately (BI_PERSONAL_POLICY), where they are drawn.
  finance_rider_debts: { permission: 'analytics:view', target: 'GEO' }, // GET /v1/analytics/rider-debts/summary
  finance_rider_cash_blocks: { permission: 'rider_cash:view', target: 'GEO', alsoRequires: ['analytics:view_personal'] }, // GET /v1/admin/rider-cash-blocks?market= (names a person)
  finance_driver_debts: { permission: 'payout:view', target: 'GEO' }, // GET /v1/finance/driver-debts?market= (D438, A-02; per-market since review W8F-07)
  // ---- W7-OPS-GOVERNANCE (D420) ---------------------------------------------------------------------
  ops_incidents: { permission: 'ops:view', target: 'GEO' }, // GET /v1/ops/incidents?market= (D416, GEO)
  route_anomalies: { permission: 'safety:view', target: 'GEO' }, // GET /v1/safety/route-anomalies?market= (D471, A-29)
  // ---- W9-RISK-AREAS-ALERTS (D474) ----
  risk_areas: { permission: 'safety:view', target: 'GEO' }, // GET /v1/risk-areas?market= (C-024, I-076)
  alert_center: { permission: 'alert:view', target: 'GEO' }, // GET /v1/alerts?market= (H-009, I-058)
  // ---- W10-MARKET-SIGNALS (D488) ----
  incentive_zones: { permission: 'incentive_zone:view', target: 'GEO' }, // GET /v1/incentive-zones?market= (doc 17 §3.2)
  // ---- GEOFENCE-EDITOR (D616) ----
  service_areas: { permission: 'service_area:view', target: 'GEO' }, // GET /v1/service-areas?market= (ADR-0121)
  // ---- PLACES (D674) ----
  places: { permission: 'places:view', target: 'GEO' }, // GET /v1/admin/places?country=|region=|market= (doc 27; the list is authorized at the scope it names)
  // ---- DEVICE-PAIRING (D561) ----
  devices: { permission: 'device:view', target: 'GEO' }, // GET /v1/ops/devices?market= (ADR-0116)
  // ---- TV-PANEL-LINKS (D647) ----
  tv_panels: { permission: 'device:view', target: 'GEO' }, // GET /v1/ops/tv-panels (ADR-0123; the list holds only the panels whose scope the grant reaches)
  // ---- W9-BROADCAST-BANNERS-AWARDS (D477) ----
  broadcasts: { permission: 'broadcast:view', target: 'GEO' }, // GET /v1/broadcasts?market=|country= (H-014)
  banners: { permission: 'banner:view', target: 'GEO' }, // GET /v1/banners (H-018)
  awards: { permission: 'award:view', target: 'GEO' }, // GET /v1/awards?market= (F-051, I-053)
  ride_chats: { permission: 'ride_chat:read', target: 'GEO' }, // GET /v1/ops/ride-chats?market= (D473; H-001, H-007)
  central_teams: { permission: 'central_team:view', target: 'GEO' }, // GET /v1/central/teams (D632; doc 25 section 7)
  central_whatsapp: { permission: 'whatsapp:view', target: 'GEO' }, // GET /v1/central/whatsapp/numbers (D636; doc 25 phase 1)
  // ---- W8-DRIVER-ELIGIBILITY (D437) -----------------------------------------------------------------
  // GET /v1/admin/kyc-catalog?country= is GEO authorized at the COUNTRY: a CITY grant never covers it.
  kyc_catalog: { permission: 'kyc_catalog:view', target: 'COUNTRY' },
  driver_vehicles: { permission: 'vehicle:view', target: 'GEO' }, // GET /v1/admin/driver-vehicles?marketId= (GEO)
  // ---- AN-BI-UI (D522) ----
  // Vello's own BI: `GET /v1/analytics/catalog` and `POST /v1/analytics/query` are `analytics:view` GEO rows (the server bounds the
  // reach by the grant's markets). Names and the CSV are separate words, checked where the control is drawn (BI_PERSONAL_POLICY, BI_EXPORT_POLICY).
  bi_panels: { permission: 'analytics:view', target: 'GEO' },
  bi_explorer: { permission: 'analytics:view', target: 'GEO' },
  bi_reports: { permission: 'analytics:view', target: 'GEO' },
  // D532: reading one's own exports (and the files delivered to one) is the read word; queueing, scheduling and templates are `BI_EXPORT_POLICY`, offered inside.
  bi_exports: { permission: 'analytics:view', target: 'GEO' },
  // ---- AN-ORPHANS (D527) ----
  // `GET /v1/analytics/lists/:list` is an `analytics:view` GEO row; the lists of people also ask the personal word (the server decides, per list).
  bi_lists: { permission: 'analytics:view', target: 'GEO' },
  // `GET /v1/ops/access/sessions` is the admins' `access:audit` word (not a read action): the server reaches COUNTRY and GLOBAL grants only (a city grant has no country to narrow to).
  access_report: { permission: 'access:audit', target: 'COUNTRY' },
};

/** The word that offers a person's name and the person/order grain in the explorer (`POST /v1/analytics/query`, in-handler). */
export const BI_PERSONAL_POLICY: ScreenPolicy = { permission: 'analytics:view_personal', target: 'GEO' };
/** The word that offers the CSV export (`POST /v1/analytics/export`). */
export const BI_EXPORT_POLICY: ScreenPolicy = { permission: 'analytics:export', target: 'GEO' };
/** The word that offers keeping one's own saved reports (`POST /v1/analytics/reports`; D523). Reading the list is `analytics:view`. */
export const BI_SAVE_POLICY: ScreenPolicy = { permission: 'analytics:save_report', target: 'GEO' };
/** D531: the word that offers setting and clearing the goals (metas) of the scorecards (`PUT /v1/analytics/targets`). Reading them is `analytics:view`. */
export const BI_TARGETS_POLICY: ScreenPolicy = { permission: 'analytics:manage_targets', target: 'GEO' };

/**
 * D411: screens that aggregate several reads, each under its own word. Such a screen is offered when the grants carry
 * AT LEAST ONE of these policies — it then shows only the sources that one reads, and leaves the others out. The four-eyes
 * inbox (Admin #61) is one: settings changes (`config:view`), payouts and cash handovers (`payout:view`), refund
 * approvals (`refund:view`), country packs (`config:view`), every one PLATFORM on the server, and (D616 round 2) a market's
 * service-area proposal (`service_area:view`, GEO) and (D737b) a market's rider-balance adjustments (`rider_wallet:approve`, GEO). Kept equal to the
 * inbox's own source table (`four-eyes-inbox.ts`) by its test.
 */
export const ANY_OF_SCREEN_POLICIES: Readonly<Record<string, readonly ScreenPolicy[]>> = {
  four_eyes_inbox: [
    { permission: 'config:view', target: 'PLATFORM' },
    { permission: 'payout:view', target: 'PLATFORM' },
    { permission: 'payout:view', target: 'PLATFORM' },
    { permission: 'refund:view', target: 'PLATFORM' },
    { permission: 'config:view', target: 'PLATFORM' },
    { permission: 'service_area:view', target: 'GEO' },
    { permission: 'rider_wallet:approve', target: 'GEO' },
  ],
  // D615 (SETUP-READINESS): the checklist reads every item under the word of the screen that shows (and fixes) it, so it is offered
  // to anyone who can read at least one: integrations, governed settings, markets, the operator directory, tariffs, the KYC catalog.
  // The server answers only the items the caller may read (an item it may not read is absent). Kept equal to the server's
  // `readBy` rows (`setup-readiness.ts`).
  platform_setup: [
    { permission: 'integration:view', target: 'PLATFORM' },
    { permission: 'config:view', target: 'PLATFORM' },
    { permission: 'market:manage', target: 'PLATFORM' },
    { permission: 'operator:manage', target: 'COUNTRY' },
    { permission: 'pricing:view', target: 'PLATFORM' },
    { permission: 'kyc_catalog:view', target: 'COUNTRY' },
  ],
};

/** Scope kinds that cover a geographic target; a MERCHANT/FLEET grant covers none of the Admin's screens. `REGION` is the state (ADR-0124). */
const GEO_SCOPES: readonly string[] = ['GLOBAL', 'COUNTRY', 'REGION', 'CITY', 'MARKET'];
/** Scope kinds that cover a country-level target (D384): never a state or a city grant (ADR-0124 §4: a REGION grant covers a state or one of its cities, not the country). */
const COUNTRY_SCOPES: readonly string[] = ['GLOBAL', 'COUNTRY'];

/**
 * The words one grant carries. The server sends them (`GET /v1/operator/grants`), and a malformed grant — a scope
 * kind its role may not hold — already arrives with an empty list. An older payload without the field falls back
 * to the role catalog, guarded by the same scope-kind rule, so the fallback can never be more generous.
 */
export function wordsOfGrant(grant: OperatorGrant): readonly string[] {
  if (grant.permissions !== undefined) return grant.permissions;
  if (!roleAllowsScopeType(grant.role, grant.scopeType === 'MARKET' ? 'CITY' : grant.scopeType)) return [];
  return permissionsForRole(grant.role);
}

/** Whether the operator's grants carry `permission` at `target` (the client-side mirror of the server's check). */
export function grantsAllow(grants: readonly OperatorGrant[], policy: ScreenPolicy): boolean {
  const covers = (g: OperatorGrant): boolean =>
    policy.target === 'PLATFORM' ? g.scopeType === 'GLOBAL' : policy.target === 'COUNTRY' ? COUNTRY_SCOPES.includes(g.scopeType) : GEO_SCOPES.includes(g.scopeType);
  return [policy.permission, ...(policy.alsoRequires ?? [])].every((word) => grants.some((g) => covers(g) && permissionGranted(wordsOfGrant(g), word)));
}

/**
 * The operator's own grants as the console knows them: the list `GET /v1/operator/grants` returned, or
 * `undefined` when that read has not answered (still loading, or it failed).
 *
 * **`undefined` is not `[]`** (review of D364). `[]` is a proven authority — an operator that really holds no
 * usable grant — and is filtered. `undefined` is an UNKNOWN authority, and filtering on it would hide the whole
 * console from every persona (including SUPER_ADMIN) on one transient failure of a single read, with the menu
 * empty and every screen claiming "no permission" — a refusal the operator does not actually have. Since this
 * filter is only a courtesy over an authority that lives on the server (every call is re-authorized there), the
 * safe answer for an unknown authority is to filter nothing and let the server refuse what it refuses.
 */
export type KnownGrants = readonly OperatorGrant[] | undefined;

/**
 * Whether a console route is one this operator can work. A route with no declared policy is always allowed; an
 * aggregating route (`ANY_OF_SCREEN_POLICIES`, D411) needs one of its sources.
 */
export function screenAllowed(grants: KnownGrants, route: string): boolean {
  if (grants === undefined) return true; // unknown authority: do not filter (see `KnownGrants`)
  const policy = SCREEN_POLICIES[route];
  if (policy !== undefined) return grantsAllow(grants, policy);
  const anyOf = ANY_OF_SCREEN_POLICIES[route];
  return anyOf === undefined || anyOf.some((p) => grantsAllow(grants, p));
}

/**
 * Where an operator lands after signing in, and after a boot that resolved the default route.
 *
 * The order is "the most useful screen this persona can actually work": the ops overview for a platform/ops
 * operator, then the support surfaces an atendente lives in, then the money screens for FINANCE, then the
 * provider queues, then whatever else the grant allows. `about` is the last resort — it needs no word, so an
 * operator with no usable grant still lands on a real page that tells it so, never on a 403.
 */
export const LANDING_PREFERENCE: readonly string[] = [
  'dashboard',
  'rides',
  'cases',
  'safety',
  'finance',
  'finance_payouts',
  'driver_approvals',
  'driver_directory',
  'passengers',
  'campaigns',
  'food_ops',
  'food_orders',
  'send_deliveries',
  'analytics',
  'registries',
  'markets',
  'integrations',
  'security',
  'about',
];

export function landingRoute(grants: KnownGrants): string {
  // D475 (A-018): a grant that names a custom access profile lands its holder on the profile's home screen — when the grants can open it.
  const home = grants?.map((g) => g.profile?.homeScreen).find((route): route is string => route !== undefined && screenAllowed(grants, route));
  return home ?? LANDING_PREFERENCE.find((route) => screenAllowed(grants, route)) ?? 'about';
}

// ---- grant-aware navigation ----------------------------------------------------------------------

/** The shape of a navigation node this filter understands (structurally the console's `NavNode`). */
export interface AccessNavNode {
  readonly id: string;
  readonly route?: string;
  readonly children?: readonly AccessNavNode[];
}

/**
 * Drop from the menu every leaf the grant cannot open, and every group left with no leaf. A group node keeps its
 * identity (and any extra fields the console renders) — only its `children` are narrowed.
 */
export function filterNavByGrants<T extends AccessNavNode>(nodes: readonly T[], grants: KnownGrants): readonly T[] {
  if (grants === undefined) return nodes; // unknown authority: offer the whole menu (see `KnownGrants`)
  const kept: T[] = [];
  for (const node of nodes) {
    if (node.children !== undefined) {
      const children = filterNavByGrants(node.children as readonly T[], grants);
      if (children.length > 0) kept.push({ ...node, children } as T);
      continue;
    }
    if (node.route !== undefined && screenAllowed(grants, node.route)) kept.push(node);
  }
  return kept;
}

/** Why a screen is refused, so the screen can say it in words instead of printing a code. */
export type RefusalReason = 'no_permission' | 'out_of_scope' | 'unavailable';

/**
 * Classify a server refusal for the "no permission" state. A `FORBIDDEN` on a screen the grant does not carry is
 * a permission problem; a `FORBIDDEN` on a screen it does carry is the scope (the wrong market selected).
 */
export function refusalReasonFor(code: string, grants: KnownGrants, route: string): RefusalReason {
  if (code !== 'FORBIDDEN' && code !== 'UNAUTHORIZED') return 'unavailable';
  return screenAllowed(grants, route) ? 'out_of_scope' : 'no_permission';
}
